/**
 * Geld (GELD) Support Registry
 *
 * Support content (hints, explanations, visuals) for GELD families.
 *
 * Module: GELD (Geld)
 * Families: 15 (registry) = 15 (taxonomy)
 * Coverage: 100%
 *
 * Created: 2026-03-25
 * Voice upgrade: 17 mei 2026 — ronde 1 + ronde 2 = 15/15 full voice
 * Tightening pass: 17 mei 2026 — ronde 3, na cheatsheet-redesign naar primary +
 *   collapsible secondaries. Per entry:
 *     • CORE hint is *de* truc — kort, sterk, hooguit 3-4 zinnen, alleen het
 *       essentiële stappenplan + één compact uitgewerkt voorbeeld.
 *     • Overlap met `defaultExplanation` en `bubbleHint` weggehaald.
 *     • Alle zinnen ≤ 14 woorden (compatible met validateHintQuality).
 *     • Optionele uitwijdingen (pitfall/example/transfer) zijn nu kort —
 *       ze worden in de UI ingeklapt en moeten in één oogopslag begrijpelijk
 *       zijn zodra je ze opent.
 *
 * Status: ✅ Voice-upgraded + tightened (alle 15 entries, lean voor primary
 * card + collapsible UI)
 */

import { FamilySupportEntry } from '../familySupportRegistry';
export const GELD_SUPPORT: Record<string, FamilySupportEntry> = {
  'GELD-TELLEN': {
    familyId: 'GELD-TELLEN',
    bubbleHint: 'Tel altijd van groot naar klein. Eerst biljetten, dan munten — dat scheelt fouten.',
    defaultExplanation: 'Tel je geld van groot naar klein. Eerst de biljetten, dan de munten. Voorbeeld: €5 + €1 + 50c + 20c = €6,70.',
    requiresHints: true,
    requiresVisual: true,
    hints: [{
      title: 'De truc: groot eerst, klein erna',
      content: '1) Leg alles op een rij: groot → klein.\n2) Tel eerst de biljetten op.\n3) Tel de munten erbij.\n\nVoorbeeld: €10 + €2 + 50c + 10c = €12,60.',
      hintType: 'core'
    }, {
      title: 'Welke munten en biljetten zijn er?',
      content: 'Munten: 1c · 2c · 5c · 10c · 20c · 50c · €1 · €2\nBiljetten: €5 · €10 · €20 · €50 · €100\n\n💡 Geen 25 of 75 cent — alleen 20 en 50.',
      hintType: 'example'
    }, {
      title: 'Niet 50c als €0,5 schrijven',
      content: "Vóór de komma: euro's. Ná de komma: altijd twee centen-cijfers.\n50c = €0,50 (niet €0,5).",
      hintType: 'pitfall'
    }]
  },
  'GELD-MAAK': {
    familyId: 'GELD-MAAK',
    bubbleHint: 'Maak een bedrag van groot naar klein. Eerst zoveel mogelijk €2 en €1, dan vul je aan.',
    defaultExplanation: 'Bouw het bedrag op van groot naar klein. Pak eerst zoveel mogelijk grote munten, vul aan met kleinere. Voorbeeld: €3,75 = €2 + €1 + 50c + 20c + 5c.',
    defaultVisual: {
      type: 'money',
      data: {
        amount: 350,
        label: 'Bedrag maken',
        showCoins: true,
        showBills: true
      },
      showWhen: 'wrong'
    },
    requiresHints: true,
    requiresVisual: true,
    hints: [{
      title: 'De truc: past er nog een grote in?',
      content: '1) Hoeveel keer €2? Pak die.\n2) Past er nog €1 bij?\n3) Vul aan met 50c, 20c, 10c, 5c, 2c, 1c.\n\nVoorbeeld: €3,75 = €2 + €1 + 50c + 20c + 5c.',
      hintType: 'core'
    }, {
      title: 'Snelle check',
      content: 'Tel je munten hardop terug. Klopt het totaal? ✅\nZo niet: pas één muntje aan.',
      hintType: 'pitfall'
    }]
  },
  'GELD-TERUG': {
    familyId: 'GELD-TERUG',
    bubbleHint: 'Wisselgeld? Tel door vanaf de prijs tot wat je betaalt. Stap voor stap naar boven.',
    defaultExplanation: 'Tel vanaf de prijs door tot het bedrag dat je betaalt. Het verschil is je wisselgeld. Voorbeeld: €6,35 → €10 = €3,65 terug.',
    defaultVisual: {
      type: 'money',
      data: {
        amount: 365,
        label: 'Wisselgeld',
        showCoins: true,
        showBills: true
      },
      showWhen: 'wrong'
    },
    requiresHints: true,
    requiresVisual: true,
    hints: [{
      title: 'De truc: doortellen in trapjes',
      content: 'Eerst tot de hele euro, dan tot het totaal.\n\n€6,35 → €6,40 (+5c)\n€6,40 → €7,00 (+60c)\n€7,00 → €10 (+€3)\nSamen: €3,65 terug ✅',
      hintType: 'core'
    }, {
      title: 'Of: gewoon aftrekken',
      content: 'Betaald − prijs.\n€10,00 − €6,35 = €3,65.',
      hintType: 'transfer'
    }]
  },
  'GELD-WISSEL': {
    familyId: 'GELD-WISSEL',
    bubbleHint: 'Wisselgeld = wat je betaalt min de prijs. Of: tel door van prijs naar betaalbedrag.',
    defaultExplanation: 'Wisselgeld is het verschil tussen wat je betaalt en de prijs. €5,00 − €3,65 = €1,35 terug.',
    defaultVisual: {
      type: 'money',
      data: {
        amount: 135,
        label: 'Wisselgeld',
        showCoins: true,
        showBills: true
      },
      showWhen: 'wrong'
    },
    requiresHints: true,
    requiresVisual: true,
    hints: [{
      title: 'De truc: aftrekken',
      content: 'Betaald − prijs = wisselgeld.\n€5,00 − €3,65 = €1,35.\n\nZet komma onder komma als je het opschrijft.',
      hintType: 'core'
    }, {
      title: 'Of: doortellen in stapjes',
      content: '€3,65 → €3,70 (+5c)\n€3,70 → €4,00 (+30c)\n€4,00 → €5,00 (+€1)\nSamen: €1,35 ✅',
      hintType: 'example'
    }, {
      title: 'Klassieke fout',
      content: 'Komma vergeten = 10× of 100× verkeerd.\nDoe €5,00 − €3,65, níet €5 − €365.',
      hintType: 'pitfall'
    }]
  },
  'GELD-KOMMA': {
    familyId: 'GELD-KOMMA',
    bubbleHint: "Komma in een bedrag? Links staan euro's, rechts centen. Tel ze apart en zet ze samen.",
    defaultExplanation: "Voor de komma staan de euro's, ná de komma de centen. €3,50 betekent 3 euro en 50 cent (samen 350 cent).",
    defaultVisual: {
      type: 'money',
      data: {
        amount: 350,
        label: 'Bedrag',
        showCoins: true,
        showBills: true
      },
      showWhen: 'wrong'
    },
    requiresHints: true,
    requiresVisual: true,
    hints: [{
      title: "De truc: links euro's, rechts centen",
      content: 'Altijd twéé cijfers ná de komma — dat zijn de centen.\n\n€3,50 = €3 + 50c\n€12,05 = €12 + 5c (let op de 0!)\n€0,75 = 75 cent',
      hintType: 'core'
    }, {
      title: 'Van centen naar euro-notatie',
      content: '350c → €3,50\n75c → €0,75\n5c → €0,05\n205c → €2,05',
      hintType: 'example'
    }, {
      title: 'Hardop lezen helpt',
      content: 'Lees de komma als "euro": €3,50 = "drie euro vijftig".\n€5 is niet €0,05 — let op de plek van de komma.',
      hintType: 'pitfall'
    }]
  },
  'GELD-WISSELEN': {
    familyId: 'GELD-WISSELEN',
    bubbleHint: 'Geld omwisselen naar een ander land? × de koers om óm te wisselen, ÷ de koers om terug.',
    defaultExplanation: 'Bij wisselen reken je met de wisselkoers. Van euro naar andere munt: keer doen. Terug naar euro: delen. Voorbeeld: €100 × 1,10 = $110.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'De truc: welke kant ga je op?',
      content: 'Euro → andere munt: × koers.\nAndere munt → euro: ÷ koers.\n\nKoers €1 = $1,10:\n€50 × 1,10 = $55\n$55 ÷ 1,10 = €50 ✅',
      hintType: 'core'
    }, {
      title: 'Snelle check',
      content: 'Welke munt is meer waard?\nIs jouw antwoord een groter of kleiner getal?\nKlopt dat met je gevoel?',
      hintType: 'pitfall'
    }]
  },
  'GELD-KOOPJES': {
    familyId: 'GELD-KOOPJES',
    bubbleHint: 'Welk pak is voordeliger? Reken altijd terug naar prijs per stuk of per kilo.',
    defaultExplanation: 'Om aanbiedingen te vergelijken reken je de prijs per stuk (of per kilo) uit. Laagste prijs per stuk = de beste deal.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'De truc: deel de totaalprijs',
      content: 'Prijs per stuk = totaalprijs ÷ aantal.\n\n3 appels voor €1,50 → €0,50 per stuk\n5 appels voor €2,00 → €0,40 per stuk\n\n5-pak is goedkoper.',
      hintType: 'core'
    }, {
      title: 'Ook bij gewicht',
      content: 'Prijs per kg = totaalprijs ÷ kilo.\n500g voor €3 = €6 per kg\n750g voor €4 ≈ €5,33 per kg ✅',
      hintType: 'example'
    }, {
      title: 'Groter ≠ altijd goedkoper',
      content: 'Een familiepak lijkt vaak voordelig, maar reken altijd om.\nSoms is de kleine verpakking juist de beste deal.',
      hintType: 'pitfall'
    }]
  },
  'GELD-BUDGET': {
    familyId: 'GELD-BUDGET',
    bubbleHint: 'Budget? Tel eerst alle uitgaven op. Daarna: budget min uitgaven = wat je overhoudt.',
    defaultExplanation: 'Bij budgetteren tel je je uitgaven op en haal je dat van je budget af. Wat overblijft kun je nog uitgeven.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'De truc: 3 stappen',
      content: '1) Tel alle uitgaven op.\n2) Budget − uitgaven.\n3) Dat is je restant.\n\nBudget €20, uitgaven €11,75.\nOver: €20 − €11,75 = €8,25 ✅',
      hintType: 'core'
    }, {
      title: 'Heb ik genoeg?',
      content: 'Uitgaven < budget → ✅ over.\nUitgaven = budget → precies genoeg.\nUitgaven > budget → ❌ tekort.',
      hintType: 'example'
    }, {
      title: 'Tekort = negatief',
      content: 'Komt €20 − €25 uit op −€5? Dan kom je €5 tekort.\nJe kunt niet alles kopen.',
      hintType: 'pitfall'
    }]
  },
  'GELD-KORTING': {
    familyId: 'GELD-KORTING',
    bubbleHint: 'Korting? Reken eerst het kortingsbedrag uit, trek dat af. Of pak gelijk wat je betaalt.',
    defaultExplanation: 'Bij korting reken je het kortingsbedrag uit en trek je dat af. 25% korting op €40 → €10 korting → €30 betalen.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'De truc: in 2 stappen',
      content: '1) Hoeveel korting? Percentage van de prijs.\n2) Prijs − korting = wat je betaalt.\n\n25% van €40 = €10\n€40 − €10 = €30 ✅',
      hintType: 'core'
    }, {
      title: 'Snel: pak gelijk wat je betaalt',
      content: 'Bij 25% korting betaal je 75%.\n75% × €40 = €30. Klaar in één stap.\n\nBetaal-% = 100% − korting-%.',
      hintType: 'transfer'
    }, {
      title: 'Handige percentages',
      content: '50% = helft (÷ 2)\n25% = kwart (÷ 4)\n10% = ÷ 10\n20% = 2 × 10%\n5% = helft van 10%',
      hintType: 'example'
    }]
  },
  'GELD-BTW': {
    familyId: 'GELD-BTW',
    bubbleHint: 'BTW erbij? × 1,21 (hoog tarief). BTW eraf? ÷ 1,21. Eten en boeken hebben 9% (× 1,09).',
    defaultExplanation: 'BTW is een belasting bovenop de prijs. Hoog tarief: 21%. Prijs zonder BTW × 1,21 = prijs mét BTW.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'De truc: keer of delen?',
      content: 'BTW erbij: × 1,21.\nBTW eraf: ÷ 1,21.\n\n€100 × 1,21 = €121 (mét BTW)\n€121 ÷ 1,21 = €100 (zonder BTW) ✅',
      hintType: 'core'
    }, {
      title: 'Twee tarieven',
      content: 'Hoog (21%): kleren, speelgoed, elektronica.\n× of ÷ 1,21\n\nLaag (9%): eten, boeken, medicijnen.\n× of ÷ 1,09',
      hintType: 'example'
    }, {
      title: '21% eraf ≠ ÷ 1,21',
      content: '€121 − 21% = €95,59 ❌\n€121 ÷ 1,21 = €100 ✅\n\nHet percentage hoort bij de prijs zónder BTW.',
      hintType: 'pitfall'
    }]
  },
  'GELD-TERUGREKENEN': {
    familyId: 'GELD-TERUGREKENEN',
    bubbleHint: 'Originele prijs zoeken na korting? Wat je betaalt is nog maar een stukje van 100%.',
    defaultExplanation: 'Bij terugrekenen weet je de nieuwe prijs en het kortingspercentage, en zoek je de prijs vóór korting. Deel door het overgebleven percentage.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'De truc: wat is er over van 100%?',
      content: '1) 100% − korting% = wat je betaalt in %.\n2) Nieuwe prijs ÷ dat % (als kommagetal).\n\n€80 na 20% korting:\n100% − 20% = 80% = 0,80\n€80 ÷ 0,80 = €100 ✅',
      hintType: 'core'
    }, {
      title: 'Check achteraf',
      content: 'Reken terug om te checken!\n€100 − 20% = €100 − €20 = €80 ✅\nKlopt het niet? Heb je per ongeluk de korting van de nieuwe prijs gerekend?',
      hintType: 'pitfall'
    }]
  },
  'GELD-SPAREN': {
    familyId: 'GELD-SPAREN',
    bubbleHint: 'Rente = je spaarbedrag × het rentepercentage. Dat krijg je erbij na een jaar.',
    defaultExplanation: 'Bij sparen krijg je rente: een percentage van je spaargeld. Rente = bedrag × percentage. Voorbeeld: €1.000 × 2% = €20 per jaar.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'De truc: rente uitrekenen',
      content: 'Rente = bedrag × percentage (als kommagetal).\n2% = 0,02. 5% = 0,05.\n\n€1.000 × 0,02 = €20 rente.\nNa 1 jaar: €1.000 + €20 = €1.020 ✅',
      hintType: 'core'
    }, {
      title: 'Meer voorbeelden',
      content: '€500 × 3% = €15\n€2.000 × 1,5% = €30\n€100 × 4% = €4\n\nHoger bedrag of % → meer rente.',
      hintType: 'example'
    }]
  },
  'GELD-RENTE-PROC': {
    familyId: 'GELD-RENTE-PROC',
    bubbleHint: 'Samengestelde rente = rente over je rente. Elk jaar groeit je geld iets sneller.',
    defaultExplanation: 'Bij samengestelde rente krijg je elk jaar rente over je nieuwe totaal — dus ook over de rente van vorig jaar. Daardoor groeit je geld steeds sneller.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'De truc: × (1 + rente) per jaar',
      content: 'Bij 5% rente: × 1,05 per jaar.\n\n€1.000 × 1,05 = €1.050\n€1.050 × 1,05 = €1.102,50\n€1.102,50 × 1,05 ≈ €1.157,63 ✅',
      hintType: 'core'
    }, {
      title: 'Waarom groeit het sneller?',
      content: 'In jaar 2 reken je over €1.050, niet €1.000.\nIn jaar 3 over €1.102,50.\nElke keer iets meer rente.',
      hintType: 'example'
    }, {
      title: 'Verschil met gewone rente',
      content: 'Gewone rente: elk jaar over het beginbedrag.\n€1.000 × 5% × 3 = €150.\n\nSamengesteld: €157,63. Verschil: €7,63.',
      hintType: 'transfer'
    }]
  },
  'GELD-BOODSCHAPPEN': {
    familyId: 'GELD-BOODSCHAPPEN',
    bubbleHint: 'Per product: prijs × aantal. Daarna alles bij elkaar. Check of je genoeg geld hebt.',
    defaultExplanation: 'Tel alle producten op: per product is dat prijs × aantal. Daarna alle bedragen optellen. Voorbeeld: 3 appels van €0,50 = €1,50.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'De truc: per product, dan optellen',
      content: '1) Per product: prijs × aantal.\n2) Tel alle deelbedragen op.\n3) Check: heb ik genoeg?\n\n3 × €0,50 + 2 × €2,25\n= €1,50 + €4,50 = €6,00 ✅',
      hintType: 'core'
    }, {
      title: '"3 voor €2" lezen',
      content: 'Dat is drié stuks samen voor €2 — niet €2 per stuk.\nPer stuk: €2 ÷ 3 ≈ €0,67.',
      hintType: 'pitfall'
    }]
  },
  'GELD-PRIJS-PER': {
    familyId: 'GELD-PRIJS-PER',
    bubbleHint: 'Welke is voordeliger? Reken eerst de prijs per stuk of per kilo uit, dan vergelijken.',
    defaultExplanation: 'Prijs per eenheid = totaalprijs ÷ aantal (of gewicht). Zo kun je verpakkingen eerlijk vergelijken.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'De truc: deel door aantal',
      content: 'Prijs per stuk = totaalprijs ÷ aantal.\nPrijs per kg = totaalprijs ÷ kilo.\n\n€6 voor 2 kg = €3 per kg.\n€4,50 voor 3 stuks = €1,50 per stuk.',
      hintType: 'core'
    }, {
      title: 'Vergelijken',
      content: 'Reken beide pakken om naar dezelfde eenheid.\n500g voor €2 = €4/kg\n750g voor €2,70 = €3,60/kg ✅',
      hintType: 'example'
    }, {
      title: 'Schap-stickers checken',
      content: 'De prijs per kg staat soms al op het schap (oranje sticker).\nMaar reken zelf bij aanbiedingen zoals "2 voor 1".',
      hintType: 'pitfall'
    }]
  }
};